import { useMemo, useState } from 'react';
import { Icon } from '../../components/Icon.tsx';
import { Button, Dialog, EmptyState, IconButton, Skeleton, StatusBadge, type Tone } from '../../components/ui.tsx';
import { t } from '../../i18n/index.ts';
import { action } from '../../lib/api.ts';
import { money } from '../../lib/format.ts';
import { useSession } from '../../lib/session.tsx';
import { ManageLayout, useAction, useRecords, useSave } from './common.tsx';
import { parseQty, unitCost, unitLabel, withUnit, type Overview, type Unit } from './Stock.tsx';

interface RecipeLine {
  item: string;
  name: string;
  unit: Unit;
  qty: number;
  cost: number;
}
interface Recipe {
  product: string;
  name: string;
  category: string | null;
  price: number;
  net: number;
  cost: number;
  food_cost: number | null;
  lines: RecipeLine[];
}
interface Category {
  id: string;
  name: string;
}

/** Food cost against the price without VAT: up to 30 % is healthy, above 40 % eats the margin. */
export function foodCostTone(pct: number | null): Tone {
  if (pct === null) return 'neutral';
  return pct <= 30 ? 'ok' : pct <= 40 ? 'warn' : 'danger';
}
function FoodCost({ pct }: { pct: number | null }) {
  const tone = foodCostTone(pct);
  if (pct === null) return <StatusBadge tone="neutral" icon="circle" label={t('recipes.none')} />;
  return <StatusBadge tone={tone} icon={tone === 'ok' ? 'check' : tone === 'warn' ? 'clock' : 'alert'} label={t('recipes.food_cost_pct', { pct: pct.toFixed(1) })} />;
}

type Filter = 'all' | 'missing' | 'high';

/** What every dish and drink is made of, what a portion costs and how much of the price it eats. */
export function Recipes() {
  const { can } = useSession();
  const { data, reload } = useAction<Recipe[]>('inventory.recipes');
  const { data: stock } = useAction<Overview>('inventory.overview');
  const { rows: categories } = useRecords<Category>('product.category', { order_by: 'sequence' });
  const [filter, setFilter] = useState<Filter>('all');
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState<Recipe | null>(null);
  const manage = can('inventory.manage');

  const groups = useMemo(() => {
    const q = search.trim().toLowerCase();
    const list = (data ?? [])
      .filter((r) => filter !== 'missing' || !r.lines.length)
      .filter((r) => filter !== 'high' || foodCostTone(r.food_cost) === 'danger')
      .filter((r) => !q || r.name.toLowerCase().includes(q) || r.lines.some((l) => l.name.toLowerCase().includes(q)));
    const names = new Map((categories ?? []).map((c) => [c.id, c.name]));
    const order = (categories ?? []).map((c) => c.id);
    const by = new Map<string, Recipe[]>();
    for (const r of list) by.set(r.category ?? '', [...(by.get(r.category ?? '') ?? []), r]);
    return [...by.entries()]
      .sort(([a], [b]) => (a === '' ? 1 : b === '' ? -1 : order.indexOf(a) - order.indexOf(b)))
      .map(([id, rows]) => ({ id, name: names.get(id) ?? t('recipes.no_category'), rows }));
  }, [data, categories, filter, search]);

  const withRecipe = (data ?? []).filter((r) => r.lines.length);
  const average = withRecipe.length
    ? Math.round((withRecipe.reduce((s, r) => s + r.cost, 0) / withRecipe.reduce((s, r) => s + r.net, 0)) * 1000) / 10
    : null;
  const missing = (data ?? []).length - withRecipe.length;
  const high = withRecipe.filter((r) => foodCostTone(r.food_cost) === 'danger').length;

  return (
    <ManageLayout title={t('manage.recipes')} subtitle={t('recipes.subtitle')}>
      {!data || !categories ? (
        <Skeleton h={420} r="var(--radius-lg)" />
      ) : (
        <div className="stock">
          <dl className="fiscal-stats">
            <div className={`fiscal-stat ${foodCostTone(average)}`}>
              <dt>{t('recipes.average')}</dt>
              <dd>{average === null ? '—' : `${average.toFixed(1)} %`}</dd>
            </div>
            <div className="fiscal-stat">
              <dt>{t('recipes.with_recipe')}</dt>
              <dd>
                {withRecipe.length} / {data.length}
              </dd>
            </div>
            <div className={`fiscal-stat${missing ? ' warn' : ''}`}>
              <dt>{t('recipes.missing')}</dt>
              <dd>{missing}</dd>
            </div>
            <div className={`fiscal-stat${high ? ' danger' : ''}`}>
              <dt>{t('recipes.high')}</dt>
              <dd>{high}</dd>
            </div>
          </dl>

          <div className="toolbar">
            <div className="segmented" role="radiogroup" aria-label={t('stock.show')}>
              {(['all', 'missing', 'high'] as const).map((f) => (
                <button key={f} role="radio" aria-checked={filter === f} className={filter === f ? 'active' : ''} onClick={() => setFilter(f)}>
                  {t(`recipes.filter_${f}`)}
                </button>
              ))}
            </div>
            <div className="search grow">
              <Icon name="search" size={18} />
              <input className="input" type="search" placeholder={t('recipes.search')} aria-label={t('recipes.search')} value={search} onChange={(e) => setSearch(e.target.value)} />
            </div>
          </div>
          <p className="muted small">{t('recipes.rule')}</p>

          {groups.length === 0 ? (
            <section className="list-card">
              <EmptyState icon="book" title={search ? t('order.no_results') : filter === 'high' ? t('recipes.none_high') : t('recipes.none_missing')} />
            </section>
          ) : (
            groups.map((g) => (
              <section key={g.id} className="list-card" aria-label={g.name}>
                <div className="list-card-head">
                  <h2>{g.name}</h2>
                </div>
                {g.rows.map((r) => (
                  <button key={r.product} className="list-row stock-row" onClick={() => setOpen(r)}>
                    <span className="list-main">
                      <span className="list-title">{r.name}</span>
                      <span className="list-sub">
                        {r.lines.length ? r.lines.map((l) => l.name).join(', ') : manage ? t('recipes.add_hint') : t('recipes.none')}
                      </span>
                    </span>
                    <span className="stock-qty">
                      <strong>{r.lines.length ? money(r.cost) : '—'}</strong>
                      <span className="muted">{t('recipes.of_price', { price: money(r.price) })}</span>
                    </span>
                    <FoodCost pct={r.food_cost} />
                  </button>
                ))}
              </section>
            ))
          )}
        </div>
      )}
      {open && stock ? <RecipeDialog recipe={open} stock={stock} manage={manage} onClose={() => setOpen(null)} onDone={reload} /> : null}
    </ManageLayout>
  );
}

function RecipeDialog({ recipe, stock, manage, onClose, onDone }: { recipe: Recipe; stock: Overview; manage: boolean; onClose: () => void; onDone: () => Promise<void> }) {
  const { busy, run } = useSave();
  const items = stock.items.filter((i) => i.active || recipe.lines.some((l) => l.item === i.id));
  const byId = new Map(stock.items.map((i) => [i.id, i]));
  const [lines, setLines] = useState(recipe.lines.map((l) => ({ item: l.item, qty: String(l.qty) })));
  const parsed = lines.map((l) => ({ item: byId.get(l.item), qty: parseQty(l.qty) }));
  const valid = parsed.every((l) => l.item && l.qty && l.qty > 0);
  const twice = new Set(lines.map((l) => l.item)).size !== lines.length;
  const cost = parsed.reduce((s, l) => s + (l.item && l.qty ? Math.round(l.qty * l.item.unit_cost) : 0), 0);
  const pct = lines.length && recipe.net > 0 ? Math.round((cost / recipe.net) * 1000) / 10 : null;
  const dirty = JSON.stringify(lines.map((l) => [l.item, parseQty(l.qty)])) !== JSON.stringify(recipe.lines.map((l) => [l.item, l.qty]));
  const set = (i: number, k: 'item' | 'qty', v: string) => setLines(lines.map((l, j) => (j === i ? { ...l, [k]: v } : l)));
  const save = () =>
    void run(async () => {
      await action('inventory.set_recipe', { product: recipe.product, lines: parsed.map((l) => ({ item: l.item!.id, qty: l.qty })) });
      await onDone();
      onClose();
    }, t('recipes.saved', { name: recipe.name }));

  return (
    <Dialog
      title={recipe.name}
      onClose={onClose}
      size="lg"
      footer={
        manage ? (
          <>
            <Button variant="ghost" onClick={onClose}>
              {t('common.cancel')}
            </Button>
            <Button variant="primary" icon="check" onClick={save} busy={busy} disabled={!valid || twice || !dirty}>
              {t('recipes.save')}
            </Button>
          </>
        ) : undefined
      }
    >
      <div className="form-stack">
        <div className="item-summary">
          <div>
            <span className="muted">{t('recipes.price')}</span>
            <strong>{money(recipe.price)}</strong>
          </div>
          <div>
            <span className="muted">{t('recipes.net')}</span>
            <strong>{money(recipe.net)}</strong>
          </div>
          <div>
            <span className="muted">{t('recipes.portion_cost')}</span>
            <strong>{lines.length ? money(cost) : '—'}</strong>
          </div>
          <FoodCost pct={pct} />
        </div>

        {lines.length === 0 ? (
          <EmptyState icon="book" title={t('recipes.empty_title')} body={manage ? t('recipes.empty_body') : undefined} />
        ) : (
          <div className="stock-lines recipe" role="table" aria-label={t('recipes.ingredients')}>
            <div className="stock-line head" role="row">
              <span role="columnheader">{t('recipes.ingredient')}</span>
              <span role="columnheader">{t('recipes.per_portion')}</span>
              <span role="columnheader" className="num">
                {t('recipes.cost')}
              </span>
              <span />
            </div>
            {lines.map((l, i) => {
              const p = parsed[i]!;
              return manage ? (
                <div key={i} className="stock-line" role="row">
                  <select className={`input${twice && lines.filter((x) => x.item === l.item).length > 1 ? ' invalid' : ''}`} aria-label={t('recipes.ingredient')} value={l.item} onChange={(e) => set(i, 'item', e.target.value)}>
                    <option value="">{t('stock.pick_item')}</option>
                    {items.map((it) => (
                      <option key={it.id} value={it.id}>
                        {it.name}
                      </option>
                    ))}
                  </select>
                  <span className="input-unit">
                    <input className={`input${l.qty && p.qty === null ? ' invalid' : ''}`} inputMode="decimal" aria-label={t('recipes.per_portion')} value={l.qty} onChange={(e) => set(i, 'qty', e.target.value)} placeholder="0" />
                    <span>{p.item ? unitLabel(p.item.unit) : ''}</span>
                  </span>
                  <span className="num">
                    {p.item && p.qty ? money(Math.round(p.qty * p.item.unit_cost)) : '—'}
                    {p.item ? <small className="muted">{unitCost(p.item.unit_cost, p.item.unit)}</small> : null}
                  </span>
                  <IconButton icon="trash" label={t('recipes.remove', { name: p.item?.name ?? '' })} onClick={() => setLines(lines.filter((_, j) => j !== i))} />
                </div>
              ) : (
                <div key={i} className="stock-line" role="row">
                  <span>{p.item?.name}</span>
                  <span>{p.item && p.qty !== null ? withUnit(p.qty, p.item.unit) : ''}</span>
                  <span className="num">{p.item && p.qty ? money(Math.round(p.qty * p.item.unit_cost)) : '—'}</span>
                  <span />
                </div>
              );
            })}
          </div>
        )}
        {twice ? (
          <p className="form-error" role="alert">
            {t('recipes.twice')}
          </p>
        ) : null}
        {manage ? (
          <Button icon="plus" className="add-line" onClick={() => setLines([...lines, { item: '', qty: '' }])}>
            {t('recipes.add_ingredient')}
          </Button>
        ) : null}
        <p className="muted small">{t('recipes.deduct_hint')}</p>
      </div>
    </Dialog>
  );
}
